import { useState, useEffect, useMemo } from 'react';
import { companyApi } from '../../api/company.api';
import { useUI } from '../../store/UIContext';
import { Btn, Skeleton, EmptyState, Modal, ConfirmDialog, Badge, Select } from '../ui/index';
import { DeviceMobile, PencilSimple } from '@phosphor-icons/react';

const emptyForm = { user_id: '', active: true };

function userDisplayLabel(u) {
  const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  return name ? `${name} (${u.username})` : u.username;
}

export default function CompanyMissionRecipientsSection({ companyId }) {
  const { addToast } = useUI();
  const [recipients, setRecipients] = useState([]);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadRecipients = () => {
    if (!companyId) return Promise.resolve();
    setLoading(true);
    return companyApi.getMissionRecipients(companyId)
      .then((res) => setRecipients(Array.isArray(res.data) ? res.data : []))
      .catch(() => addToast('خطا در دریافت مسئولین تایید ماموریت', 'error'))
      .finally(() => setLoading(false));
  };

  const loadUsers = () => {
    if (!companyId) return Promise.resolve();
    setUsersLoading(true);
    return companyApi.getCompanyUsers(companyId)
      .then((res) => setCompanyUsers(Array.isArray(res.data) ? res.data : []))
      .catch(() => addToast('خطا در دریافت کاربران شرکت', 'error'))
      .finally(() => setUsersLoading(false));
  };

  useEffect(() => {
    loadRecipients();
    loadUsers();
  }, [companyId]);

  const openAdd = () => { setEditing({}); setForm(emptyForm); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ user_id: r.user_id ? String(r.user_id) : '', active: r.active !== false });
  };

  const selectedUser = useMemo(
    () => companyUsers.find((u) => String(u.id) === String(form.user_id)),
    [companyUsers, form.user_id],
  );

  const usedUserIds = useMemo(() => {
    const ids = new Set(
      recipients
        .filter((r) => r.user_id && r.recipient_id !== editing?.recipient_id)
        .map((r) => String(r.user_id)),
    );
    return ids;
  }, [recipients, editing?.recipient_id]);

  const userOptions = useMemo(() => {
    const opts = [{ value: '', label: 'انتخاب کاربر شرکت…' }];
    companyUsers.forEach((u) => {
      if (usedUserIds.has(String(u.id))) return;
      const phoneNote = u.phone_number ? '' : ' — بدون موبایل';
      opts.push({ value: String(u.id), label: `${userDisplayLabel(u)}${phoneNote}` });
    });
    if (editing?.user_id && !opts.some((o) => o.value === String(editing.user_id))) {
      const legacy = companyUsers.find((u) => String(u.id) === String(editing.user_id));
      opts.push({
        value: String(editing.user_id),
        label: legacy ? userDisplayLabel(legacy) : (editing.name || `کاربر #${editing.user_id}`),
      });
    }
    return opts;
  }, [companyUsers, usedUserIds, editing]);

  const save = async () => {
    if (!form.user_id) {
      addToast('یک کاربر از لیست شرکت انتخاب کنید', 'error');
      return;
    }
    if (selectedUser && !selectedUser.phone_number) {
      addToast('کاربر انتخاب‌شده شماره موبایل ندارد', 'error');
      return;
    }
    setSaving(true);
    try {
      await companyApi.saveMissionRecipient({
        company_id: companyId,
        recipient_id: editing?.recipient_id || undefined,
        user_id: Number(form.user_id),
        active: form.active ? 1 : 0,
      });
      addToast(editing?.recipient_id ? 'مسئول بروزرسانی شد' : 'مسئول افزوده شد', 'success');
      setEditing(null);
      loadRecipients();
    } catch (err) {
      const msg = err?.response?.data;
      addToast(typeof msg === 'string' && msg ? msg : 'خطا در ذخیره مسئول', 'error');
    }
    setSaving(false);
  };

  const remove = async () => {
    try {
      await companyApi.deleteMissionRecipient({ company_id: companyId, recipient_id: deleteTarget.recipient_id });
      addToast('مسئول حذف شد', 'success');
      loadRecipients();
    } catch {
      addToast('خطا در حذف مسئول', 'error');
    }
  };

  const label = { fontSize: 12, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 5 };
  const phoneDisplay = selectedUser?.phone_number || (editing?.recipient_id && !selectedUser ? editing.phone : '');

  return (
    <div style={{ background: '#fff', borderRadius: 14, border: '1px solid rgba(35,58,122,0.1)', padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 10, flexWrap: 'wrap' }}>
        <h4 style={{ fontSize: 13, fontWeight: 700, color: '#64748b', margin: 0, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          مسئولین تایید ماموریت
        </h4>
        <Btn size="sm" onClick={openAdd} disabled={usersLoading || companyUsers.length === 0}>+ افزودن مسئول</Btn>
      </div>
      <p style={{ fontSize: 11, color: '#94a3b8', margin: '0 0 14px', lineHeight: 1.6 }}>
        پس از پایان ماموریت، کد OTP به اولین مسئول فعال این لیست ارسال می‌شود. مسئول باید از کاربران ثبت‌شدهٔ همان شرکت انتخاب شود.
      </p>

      {loading ? (
        <Skeleton h={60} radius={10} />
      ) : recipients.length === 0 ? (
        <EmptyState icon={<DeviceMobile size={44} weight="regular" />} title="مسئول تاییدی تعریف نشده" subtitle="یک کاربر از لیست کاربران شرکت انتخاب کنید" />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {recipients.map((r) => (
            <div key={r.recipient_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '10px 12px', background: '#f8faff', border: '1px solid #e2e8f0', borderRadius: 10, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{r.name}</div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 2, direction: 'ltr', textAlign: 'left' }}>{r.phone}</div>
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <Badge variant={r.active ? 'green' : 'gray'}>{r.active ? 'فعال' : 'غیرفعال'}</Badge>
                <Btn size="sm" variant="secondary" onClick={() => openEdit(r)}><PencilSimple size={13} weight="regular" /></Btn>
                <Btn size="sm" variant="danger" onClick={() => setDeleteTarget(r)}>🗑️</Btn>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing?.recipient_id ? 'ویرایش مسئول' : 'افزودن مسئول تایید'} width={420}>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {usersLoading ? (
            <Skeleton h={40} radius={8} />
          ) : companyUsers.length === 0 ? (
            <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>برای این شرکت کاربری ثبت نشده است.</p>
          ) : (
            <>
              <Select
                label="کاربر شرکت"
                value={form.user_id}
                onChange={(e) => setForm((p) => ({ ...p, user_id: e.target.value }))}
                options={userOptions}
              />
              {phoneDisplay && (
                <div>
                  <span style={label}>شماره موبایل (از پروفایل کاربر)</span>
                  <div style={{ fontSize: 13, color: '#334155', direction: 'ltr', textAlign: 'left', padding: '10px 14px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                    {phoneDisplay}
                  </div>
                </div>
              )}
            </>
          )}
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#334155' }}>
            <input type="checkbox" checked={form.active} onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))} />
            فعال (دریافت OTP)
          </label>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn variant="ghost" onClick={() => setEditing(null)}>انصراف</Btn>
            <Btn onClick={save} loading={saving} disabled={usersLoading || companyUsers.length === 0}>ذخیره</Btn>
          </div>
        </div>
      </Modal>

      <ConfirmDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={remove} danger
        title="حذف مسئول" message={`آیا از حذف «${deleteTarget?.name || ''}» مطمئن هستید؟`} />
    </div>
  );
}
